<template>
  <div class="utm-setting"
       :class="{ confirming } ">
    <div class="step loading"
         v-if="loading">
      <ui-loading-circle :showLoadingText="true" />
    </div>
    <div v-else-if="confirming"
         class="confirm">
      <div class="confirm-icon">
        <i class="icon icon--dialog-warning"></i>
      </div>
      <div class="confirm-description"
           v-html="confirmOption.description"></div>
      <div class="confirm-buttons">
        <ui-button @click="confirming = false">
          {{ $t('common.no') }}
        </ui-button>
        <ui-button @click="confirmOption.confirm"
                   :primary2="true">
          {{ $t('common.yes') }}
        </ui-button>
      </div>
    </div>
    <div class="step step-1"
         v-else-if="step === 1">
      <div class="utm-header">
        <i class="icon icon--utm"></i>
        <h3>
          <span v-html="$t('campaignTargeting.utm.name')"></span>
          <ui-icon-tooltip :iconSize="'normal'"
                           arrowBottom
                           arrowCenter>
            <span v-html="$t('campaignTargeting.utm.tooltip')"></span>
          </ui-icon-tooltip>
        </h3>
        <p v-html="$t('campaignTargeting.utm.description')"></p>
      </div>
      <div class="utm-body">
        <div v-if="hasData"
             class="utm-selection">
          <button v-for="tag of tagList"
                  :key="`utmButton${tag}`"
                  @click="showList(tag)"
                  :class="{ selected: selectedUtm[tag] && selectedUtm[tag].length > 0 }">
            <span class="name"
                  v-html="$t('campaignTargeting.utm.' + tag)"></span>
            <span v-if="selectedUtm[tag] && selectedUtm[tag].length > 0"
                  class="count">
              <span class="check">
                <i class="icon icon--check-white"></i>
              </span>
              <span class="number">
                {{ selectedUtm[tag].length | thousand }}
              </span>
              {{ $t('common.selected') }}
            </span>
            <i v-else
               class="icon icon--plus"></i>
          </button>
        </div>
        <div v-else
             class="utm-empty">
          <i class="icon icon--empty"></i><br>
          {{ $t('common.noCollectedData') }}
        </div>
      </div>
      <div class="utm-footer">
        <ui-button @click="onCloseLayer(false, true)">{{ $t('common.close') }}</ui-button>
      </div>
    </div>
    <div class="step step-2"
         v-else-if="step === 2">
      <div class="utm-header">
        <button type="button"
                class="btn-close"
                @click="onCloseLayer(true, true)">
          <i class="icon icon--close-size16"></i>
        </button>
        <h2 v-html="$t('campaignTargeting.utm.selecting.title', { tag: selectedTag })"></h2>
        <p v-html="$t('campaignTargeting.utm.selecting.description', { tag: selectedTag })"></p>
      </div>
      <div class="utm-body"
           :key="resetKey">
        <ui-check-selectbox v-if="listItems"
                            :width="'100%'"
                            :height="'315px'"
                            :full="true"
                            :selectAll="true"
                            :maxHeight="315"
                            :search="true"
                            :value="selectingItem"
                            :items="items"
                            :showCount="true"
                            :unselectLastOne="true"
                            @input="onSelectedUtmChange"/>
        <div v-else
             class="empty">
          <i class="icon icon--empty"></i><br>
          {{ $t('common.noCollectedData') }}
        </div>
      </div>
      <div class="utm-footer">
        <ui-button class="back"
                   :iconName="'icon--arrow-left-black'"
                   @click="showIndex">{{ $t('common.back') }}</ui-button>
        <ui-button @click="resetSelected"
                   :iconName="'icon--reset'"
                   :disabled="!selectingItem || selectingItem.length < 1">{{ $t('common.reset') }}</ui-button>
        <ui-button @click="applySelected"
                   :disabled="!listItems || listItems.length < 1 || JSON.stringify(selectingItem) === JSON.stringify(selectedUtm[selectedTag])"
                   :primary2="true">{{ $t('common.apply') }}</ui-button>
      </div>
    </div>
  </div>
</template>

<script>
import { compare09azAZ } from '@/libs/utils'
import { getUtm } from '@/api/report'
import { UTM_TAGS } from '@/libs/constants'
import UiButton from '@/components/ui/UiButton'
import UiCheckSelectbox from '@/components/ui/UiCheckSelectbox'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle'

export default {
  name: 'UtmSetting',
  components: {
    UiButton,
    UiCheckSelectbox,
    UiIconTooltip,
    UiLoadingCircle
  },
  props: ['aid', 'startDate', 'endDate', 'utmCache', 'otherKeySet'],
  methods: {
    showList (tag) {
      this.selectedTag = tag
      this.setUtmData(this.utmData)
    },
    setUtmData (result) {
      let listItems = {}
      let hasItems = false
      for (const item of result[this.selectedTagKey]) {
        if (item) {
          const params = Object.keys(item)
          if (params.length > 1 || !params.includes('__null__')) {
            Object.assign(listItems, item)
            hasItems = true
          }
        }
      }
      if (hasItems) {
        this.listItems = { '': {} }
        this.listItems[''][this.$t('campaignTargeting.utm.nullInflow', { tag: this.selectedTag })] = '__null__'

        if (listItems[''] !== undefined || listItems['__blank__'] !== undefined) {
          this.listItems[''][this.$t('campaignTargeting.utm.blankInflow', { tag: this.selectedTag })] = '__blank__'
        }

        const keys = Object.keys(listItems)
        keys.sort(compare09azAZ)
        keys.forEach(key => {
          if (key && key !== '__blank__' && key !== '__null__' && listItems[key]) {
            const char = key.charAt(0).toLowerCase()
            if (!this.listItems[char]) {
              this.listItems[char] = {}
            }
            this.listItems[char][key] = listItems[key]
          }
        })
        if (this.selectedUtm[this.selectedTag]) {
          this.selectedUtm[this.selectedTag].sort()
        }
        this.selectingItem = this.selectedUtm[this.selectedTag]
      } else {
        this.listItems = false
        this.selectingItem = []
      }
      this.step = 2
      this.loading = false
    },
    onSelectedUtmChange (value) {
      this.selectingItem = value
      this.selectingItem.sort()
    },
    showIndex () {
      const selectedUtm = this.selectedUtm[this.selectedTag] || []
      const selectingItem = this.selectingItem || []
      if (this.selectedTag && JSON.stringify(selectedUtm) !== JSON.stringify(selectingItem)) {
        this.showConfirm(this.$t('dialog.campaignTargeting.noAppliedAndGoBack.desc'), () => {
          this.resetSelected()
          this.step = 1
          this.selectedTag = null
          this.confirming = false
        })
      } else {
        this.step = 1
        this.selectedTag = null
      }
    },
    resetSelected () {
      this.selectingItem = []
      this.resetKey = new Date().toString()
    },
    applySelected () {
      if (this.otherKeySett) {
        this.showConfirm(this.$t('dialog.campaignTargeting.applyNew.desc'),
          () => {
            this.otherKeySett = false
            this.selectedUtm[this.selectedTag] = [...this.selectingItem]
            this.confirming = false
            this.step = 1
            this.selectedTag = null
          })
      } else {
        this.selectedUtm[this.selectedTag] = [...this.selectingItem]
        this.step = 1
        this.selectedTag = null
      }
    },
    onCloseLayer (reset, close) {
      let selectedUtm = this.selectedUtm
      let selectedUtmExist = false
      if (selectedUtm) {
        for (const tag of this.tagList) {
          if (selectedUtm[tag] && selectedUtm[tag].length > 0) {
            selectedUtmExist = true
          }
        }
      }
      if (!selectedUtmExist) {
        selectedUtm = null
      }
      if (reset) {
        if (JSON.stringify(selectedUtm) !== JSON.stringify(this.initialUtm) ||
          (this.selectedTag && ((!this.initialUtm && this.selectingItem.length > 0) || (this.initialUtm && JSON.stringify(this.initialUtm[this.selectedTag]) !== JSON.stringify(this.selectingItem))))) {
          this.showConfirm(this.$t('dialog.campaignTargeting.noAppliedAndClose.desc'), () => { this.$emit('onCloseLayer') })
        } else {
          this.$emit('onSetUtm', selectedUtm)
          if (close) {
            this.$emit('onCloseLayer')
          }
        }
      } else {
        this.$emit('onSetUtm', selectedUtm)
        if (close) {
          this.$emit('onCloseLayer')
        }
      }
    },
    showConfirm (description, confirm) {
      this.confirmOption.description = description
      this.confirmOption.confirm = confirm
      this.confirming = true
    }
  },
  created () {
    this.tagList = UTM_TAGS
    this.otherKeySett = this.otherKeySet

    this.initialUtm = this.utmCache
    for (const tag of UTM_TAGS) {
      this.selectedUtm[tag] = this.initialUtm ? this.initialUtm[tag] : []
    }

    this.loading = true
    this.step = 1
    getUtm({
      startDate: this.startDate,
      endDate: this.endDate,
      aid: this.aid
    })
      .then(result => {
        this.utmData = result
        this.loading = false
      })
  },
  computed: {
    hasData () {
      if (!this.utmData) {
        return false
      }
      let has = false
      if (typeof this.utmData === 'object') {
        Object.values(this.utmData).forEach(params => {
          if (params && typeof params === 'object') {
            if (params.length > 1 || (params.length < 2 && params[0] && !Object.keys(params[0]).includes('__null__'))) {
              has = true
            }
          }
        })
      }
      return has
    },
    selectedTagKey () {
      return 'utm_' + this.selectedTag.toLowerCase()
    },
    items () {
      if (!this.listItems) {
        return false
      }

      let list = Object.entries(this.listItems)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([key, value]) => {
          return {
            value: key,
            text: key,
            items: Object.entries(value).map(([k, v]) => {
              return {
                value: v === null || v === '' || v === '__blank__' || v === '__null__' ? v : k,
                text: k
              }
            })
          }
        })

      return list
    }
  },
  data () {
    return {
      step: 1,
      loading: false,
      initialUtm: null,
      selectedUtm: {},
      selectedTag: null,
      listItems: false,
      selectingItem: [],
      confirming: false,
      otherKeySett: false,
      confirmOption: {
        description: '',
        confirm: () => {}
      },
      utmData: null,
      resetKey: new Date().toString()
    }
  }
}
</script>

<style lang="scss" scoped>
.utm-setting {
  width: 731px;
  height: 526px;
  border-radius: 14px;
  box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
  background-color: #ffffff;

  &.confirming {
    position: relative;
    width: 360px;
    height: 270px;

    .confirm {
      opacity: 1;
      z-index: 10;
    }

    .step {
      opacity: 0;
      z-index: 1;
    }
  }

  .confirm {
    position: relative;
    text-align: center;
    opacity: 0;

    .confirm-icon {
      padding-top: 40px;
    }

    .confirm-description {
      font-size: 14px;
      color: #444444;
      line-height: 1.43;
      margin-top: 13px;
      padding: 0 38px;
      height: 100px;
    }

    .confirm-buttons {
      .ui-btn {
        margin: 0 2px 0 3px;
        width: 86px;
      }
    }
  }

  .step {
    position: relative;
    background-color: #ffffff;
    border-radius: 15px;
    width: 100%;
    height: 100%;

    &.loading {
      z-index: 100;
    }

    &.step-1 {
      .utm-header {
        position: relative;
        margin: 0 30px;
        border-bottom: 1px solid #bbbbbb;

        .icon {
          @include positionYcenter;
          top: calc(50% + 4px);
          border-right: 1px solid #d5d5d5;
          background-position: center left;
          height: 54px;
          padding-right: 20px;
        }

        h3 {
          font-size: 18px;
          color: #444444;
          line-height: 1.17;
          padding: 34px 0 0 87px;

          .tooltip {
            top: 2px;
            left: 6px;

            & /deep/ {
              .icon {
                width: 15px;
                height: 15px;
                background-size: 100% 100%;
              }

              .bubble {
                margin-bottom: 3px !important;
                margin-left: -10.5px !important;
              }
            }
          }
        }

        p {
          font-size: 13px;
          color: #444444;
          line-height: 1.38;
          padding: 6px 0 23px 88px;

          & /deep/ span {
            color: #ec0047;
          }
        }
      }

      .utm-body {
        margin: 105px 0 112px 0;
        white-space: nowrap;

        .utm-selection {
          text-align: center;
          margin: 5px 0;

          button {
            position: relative;
            vertical-align: top;
            width: 125px;
            height: 125px;
            margin: 1px 6px 1px 5px;
            border: 2px dotted #c3c3c3;
            border-radius: 10px;
            transition: border-color 0.2s ease,
            background-color 0.2s ease,
            box-shadow 0.2s ease;

            .name {
              font-size: 15px;
              color: #444444;
              display: block;
              margin: 5px 0 12px;
            }

            .count {
              font-size: 12px;
              color: #444444;
              margin-top: -5px;
              margin-bottom: 10px;
              display: block;
              text-align: center;

              .number {
                font-size: 14px;
                font-weight: bold;
                margin-bottom: 7px;
                display: block;
                color: #3eaa08;
              }

              .check {
                position: absolute;
                top: -1px;
                right: -1px;
                width: 19px;
                height: 19px;
                padding: 4px 4px 16px 16px;
                border-top-right-radius: 10px;
                background: url('../../../assets/img/campaign-button-bg.svg') transparent no-repeat right -30px top -30px;
                transition: background-position 0.2s ease;

                i {
                  width: 16px;
                  height: 16px;
                }
              }
            }

            i {
              width: 20px;
              height: 20px;
            }

            &.selected {
              border: 2px solid #3eaa08;
              background-color: rgba(62, 170, 8, 0.03);

              .count {
                .check {
                  background-position: right 0 top 0;
                }
              }
            }

            &:hover {
              border: 2px solid #0274ff;
              background-color: #ffffff;
              box-shadow: 0 8px 12px -2px rgba(0, 0, 0, 0.25);

              .count {
                .check {
                  background-position: right -30px top -30px;
                }
              }
            }
          }
        }

        .utm-empty {
          height: 127px;
          text-align: center;
          line-height: 1.46;
          font-size: 14px;
          color: #555555;

          i {
            margin-top: 3px;
            margin-bottom: 3px;
          }
        }
      }

      .utm-footer {
        margin: 62px 15px 20px;
        text-align: center;
        position: relative;

        .ui-btn {
          margin: 0 2px 0 3px;
          width: 95px;

          &.back {
            position: absolute;
            top: 0;
            left: 0;
            border: none;
            font-size: 13px;
            width: 75px;
            text-align: center;
          }
        }
      }
    }

    &.step-2 {
      .utm-header {
        position: relative;
        margin: 0 32px;

        h2 {
          font-size: 18px;
          color: #444444;
          padding-top: 40px;

          & /deep/ span {
            color: #ec0047;
          }
        }

        p {
          font-size: 13px;
          line-height: 1.08;
          color: #888888;
          margin: 10px 0 22px;
        }

        .btn-close {
          position: absolute;
          top: 12px;
          right: -19px;
        }
      }

      .utm-body {
        padding: 14px 30px;

        .empty {
          width: 100%;
          height: 312px;
          border-radius: 5px;
          border: 1px solid #d7d7d7;
          background-color: #f4f4f4;
          line-height: 1.77;
          color: #666666;
          font-size: 14px;
          text-align: center;
          margin-bottom: 3px;

          i {
            margin-top: 104px;
          }
        }
      }

      .utm-footer {
        margin: 14px 15px;
        text-align: center;
        position: relative;

        .ui-btn {
          margin: 0 2px 0 3px;
          width: 95px;

          &.back {
            position: absolute;
            top: 0;
            left: 0;
            border: none;
            font-size: 13px;
            width: 75px;
            text-align: center;
          }
        }
      }
    }
  }
}
</style>
